/* =====================================================================
 * AIFloatingToolbar — 텍스트 선택 시 AI 편집 기능 툴바
 * ===================================================================== */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import type { Editor } from '@tiptap/react';
import {
  Sparkles,
  PenLine,
  ArrowUpRight,
  ArrowDownRight,
  Languages,
  FileText,
  Wand2,
  CheckCircle2,
  X,
  Loader2,
  SpellCheck,
} from 'lucide-react';
import { streamAgent } from '@auw/api-client';
import type { AgentRequest } from '@auw/workspace-utils';
import { useDocumentStore } from '@auw/stores';
import { useFeature } from '@auw/feature-flags';
import { buildAIQuery } from '@auw/workspace-utils';
import { getUserLocale } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type AIAction =
  | 'improve'
  | 'fix-grammar'
  | 'make-shorter'
  | 'make-longer'
  | 'translate'
  | 'summarize'
  | 'edit';

interface ActionItem {
  id: AIAction;
  label: string;
  icon: React.ReactNode;
}

const AI_ACTIONS: ActionItem[] = [
  { id: 'improve', label: '개선하기', icon: <Wand2 size={14} /> },
  { id: 'fix-grammar', label: '문법 수정', icon: <SpellCheck size={14} /> },
  { id: 'make-shorter', label: '짧게', icon: <ArrowDownRight size={14} /> },
  { id: 'make-longer', label: '길게', icon: <ArrowUpRight size={14} /> },
  { id: 'summarize', label: '요약', icon: <FileText size={14} /> },
  { id: 'translate', label: '번역', icon: <Languages size={14} /> },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface AIFloatingToolbarProps {
  editor: Editor;
}

export const AIFloatingToolbar: React.FC<AIFloatingToolbarProps> = ({
  editor,
}) => {
  /* P11-5g: when `mode.doc-agent.aiAssist` is off the toolbar never
   * mounts — guards against any host that wires it up unconditionally. */
  const aiAssistEnabled = useFeature('mode.doc-agent.aiAssist');
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showResult, setShowResult] = useState(false);
  const [resultText, setResultText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [translateLang, setTranslateLang] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const { setAIStreaming } = useDocumentStore();

  /* ── 텍스트 선택 감지 ── */
  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      const { from, to, empty } = editor.state.selection;

      if (empty || from === to) {
        // 스트리밍 결과 표시 중이면 닫지 않음
        if (!showResult) {
          setIsVisible(false);
        }
        return;
      }

      const text = editor.state.doc.textBetween(from, to, ' ');
      if (text.trim().length < 2) {
        if (!showResult) setIsVisible(false);
        return;
      }

      setSelectedText(text);

      const coords = editor.view.coordsAtPos(from);
      const editorRect = editor.view.dom.getBoundingClientRect();

      setPosition({
        top: coords.top - editorRect.top - 48,
        left: coords.left - editorRect.left,
      });
      setIsVisible(true);
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, showResult]);

  /* ── AI 명령 실행 ── */
  const executeAI = useCallback(
    async (action: AIAction, lang?: string) => {
      if (!selectedText.trim()) return;

      setShowResult(true);
      setResultText('');
      setIsStreaming(true);
      setAIStreaming(true);

      abortRef.current = new AbortController();

      const query = buildAIQuery(action, selectedText, lang);
      const request: AgentRequest = {
        query,
        workspace_type: 'document',
        stream: true,
        user_locale: getUserLocale(),
      };

      try {
        let accumulated = '';
        for await (const chunk of streamAgent(request, abortRef.current.signal)) {
          if (chunk.status === 'in_progress') {
            const data = chunk.data as { summary?: string; detail?: unknown };
            if (data.summary) {
              accumulated += data.summary;
              setResultText(accumulated);
            }
          } else if (chunk.status === 'completed') {
            const data = chunk.data as { content?: string };
            if (data.content) {
              setResultText(data.content);
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setResultText('AI 처리 중 오류가 발생했습니다.');
        }
      } finally {
        setIsStreaming(false);
        setAIStreaming(false);
      }
    },
    [selectedText, setAIStreaming],
  );

  /* ── 결과 적용 ── */
  const applyResult = useCallback(() => {
    if (!resultText) return;

    const { from, to } = editor.state.selection;
    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent(resultText)
      .run();

    setShowResult(false);
    setResultText('');
    setIsVisible(false);
  }, [editor, resultText]);

  /* ── 취소 ── */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setShowResult(false);
    setResultText('');
    setIsStreaming(false);
    setAIStreaming(false);
    setIsVisible(false);
    setTranslateLang(null);
  }, [setAIStreaming]);

  /* ── 번역 언어 선택 ── */
  const handleTranslate = useCallback(() => {
    setTranslateLang('show');
  }, []);

  const selectLanguage = useCallback(
    (lang: string) => {
      setTranslateLang(null);
      executeAI('translate', lang);
    },
    [executeAI],
  );

  /* ── doc:ai-command 이벤트 리스너 (슬래시 명령 연동) ── */
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.type) return;

      const docContent = editor.getText();
      const query = buildAIQuery(detail.type, docContent);

      const request: AgentRequest = {
        query,
        workspace_type: 'document',
        stream: true,
        user_locale: getUserLocale(),
      };

      setShowResult(true);
      setResultText('');
      setIsStreaming(true);
      setIsVisible(true);
      setAIStreaming(true);

      const coords = editor.view.coordsAtPos(editor.state.selection.from);
      const editorRect = editor.view.dom.getBoundingClientRect();
      setPosition({
        top: coords.top - editorRect.top + 24,
        left: coords.left - editorRect.left,
      });

      abortRef.current = new AbortController();

      (async () => {
        try {
          let accumulated = '';
          for await (const chunk of streamAgent(request, abortRef.current!.signal)) {
            if (chunk.status === 'in_progress') {
              const data = chunk.data as { summary?: string };
              if (data.summary) {
                accumulated += data.summary;
                setResultText(accumulated);
              }
            } else if (chunk.status === 'completed') {
              const data = chunk.data as { content?: string };
              if (data.content) {
                setResultText(data.content);
              }
            }
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name !== 'AbortError') {
            setResultText('AI 처리 중 오류가 발생했습니다.');
          }
        } finally {
          setIsStreaming(false);
          setAIStreaming(false);
        }
      })();
    };

    document.addEventListener('doc:ai-command', handler);
    return () => document.removeEventListener('doc:ai-command', handler);
  }, [editor, setAIStreaming]);

  if (!aiAssistEnabled) return null;
  if (!isVisible) return null;

  const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'ko', label: '한국어' },
    { code: 'ja', label: '日本語' },
    { code: 'zh', label: '中文' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
  ];

  return (
    <FloatingWrap style={{ top: position.top, left: position.left }}>
      {/* ── AI 결과 패널 ── */}
      {showResult && (
        <ResultPanel ref={resultRef}>
          <ResultHeader>
            <ResultTitle>
              <Sparkles size={14} />
              AI 결과
              {isStreaming && <SpinnerIcon><Loader2 size={14} /></SpinnerIcon>}
            </ResultTitle>
            <ResultActions>
              {!isStreaming && resultText && (
                <ApplyButton onClick={applyResult}>
                  <CheckCircle2 size={14} />
                  적용
                </ApplyButton>
              )}
              <CloseButton onClick={cancel}>
                <X size={14} />
              </CloseButton>
            </ResultActions>
          </ResultHeader>
          <ResultContent>
            {resultText || (isStreaming ? 'AI가 작성 중입니다...' : '')}
          </ResultContent>
        </ResultPanel>
      )}

      {/* ── 번역 언어 선택 ── */}
      {translateLang === 'show' && (
        <LangMenu>
          {LANGUAGES.map((l) => (
            <LangItem key={l.code} onClick={() => selectLanguage(l.code)}>
              {l.label}
            </LangItem>
          ))}
        </LangMenu>
      )}

      {/* ── 기본 AI 액션 버튼 ── */}
      {!showResult && (
        <ActionBar>
          <AIBadge>
            <Sparkles size={12} />
            AI
          </AIBadge>
          {AI_ACTIONS.map((action) =>
            action.id === 'translate' ? (
              <ActionButton key={action.id} onClick={handleTranslate}>
                {action.icon}
                {action.label}
              </ActionButton>
            ) : (
              <ActionButton
                key={action.id}
                onClick={() => executeAI(action.id)}
              >
                {action.icon}
                {action.label}
              </ActionButton>
            ),
          )}
        </ActionBar>
      )}
    </FloatingWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const FloatingWrap = styled.div`
  position: absolute;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  white-space: nowrap;
`;

const AIBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #8b5cf6;
  letter-spacing: 0.04em;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  white-space: nowrap;
  transition: all 100ms;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ResultPanel = styled.div`
  width: 480px;
  max-height: 320px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ResultTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #8b5cf6;
`;

const SpinnerIcon = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const ResultActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ApplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background: #8b5cf6;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms;

  &:hover {
    background: #7c3aed;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }
`;

const ResultContent = styled.div`
  padding: 14px;
  font-size: 0.875rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textPrimary};
  max-height: 240px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const LangMenu = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
  width: 160px;
`;

const LangItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }
`;
